import { useMutation } from "@tanstack/react-query";
import { FinancialConnection } from "@common/types/financialConnection";
import {
    fixStripeConnection,
    createStripeExpressConnection,
    remintStripeExpressAccountLink,
    createStripeExpressLoginLink,
    refreshStripeExpressCache,
} from "@lib/stripe";
import { StripeExpressReturnTo } from "@common/schemas/domains/stripe/stripe-express.schema";
import { clearConnectionErrorInQueryData } from "@queries/entitiesAccounts";
import {
    queryClient,
    stripeConnectionsQueryKey,
    financialConnectionsQueryKey,
} from "../queryClient";

interface CreateStripeExpressPayload {
    defaultEntityId: number;
    returnTo: StripeExpressReturnTo;
    invoiceId?: string;
}

export function useCreateStripeExpressMutation() {
    return useMutation({
        mutationFn: (payload: CreateStripeExpressPayload) =>
            createStripeExpressConnection(payload),
    });
}

interface RemintStripeExpressPayload {
    connectionId: number;
    returnTo: StripeExpressReturnTo;
    invoiceId?: string;
}

export function useRemintStripeExpressMutation() {
    return useMutation({
        mutationFn: ({
            connectionId,
            returnTo,
            invoiceId,
        }: RemintStripeExpressPayload) =>
            remintStripeExpressAccountLink(connectionId, {
                returnTo,
                invoiceId,
            }),
    });
}

export function useStripeExpressLoginLinkMutation() {
    return useMutation({
        mutationFn: (connectionId: number) =>
            createStripeExpressLoginLink(connectionId),
    });
}

export function useRefreshStripeExpressCacheMutation() {
    return useMutation({
        mutationFn: (connectionId: number) =>
            refreshStripeExpressCache(connectionId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [financialConnectionsQueryKey],
            });
        },
    });
}

interface FixStripeConnectionPayload {
    connection: FinancialConnection;
    code: string;
}
export function useFixStripeConnectionsMutation(onSuccess?: () => unknown) {
    return useMutation({
        mutationFn: (payload: FixStripeConnectionPayload) =>
            fixStripeConnection(payload.connection.id, { code: payload.code }),
        onSuccess: (connected) => {
            if (!connected) {
                return;
            }
            clearConnectionErrorInQueryData(queryClient, connected.id);
            onSuccess?.();
            queryClient.setQueryData<FinancialConnection[] | undefined>(
                stripeConnectionsQueryKey,
                (connections) =>
                    connections?.map((c) =>
                        c.id === connected.id
                            ? {
                                  ...c,
                                  accounts: connected.accounts,
                                  error: null,
                              }
                            : c,
                    ) ?? [],
            );
        },
    });
}
